import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Order, OrderStatus } from '../../types/order';
import Modal from '../common/Modal';
import { 
  ShoppingCartIcon,
  CalendarIcon,
  TruckIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  UserIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/formatters';

interface OrderFormProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (orderId: string, updatedData: Partial<Order>) => void;
}

interface OrderFormData {
  status: OrderStatus;
  requestedDeliveryDate?: string;
  confirmedDeliveryDate?: string;
  deliveryAddress?: string;
  deliveryInstructions?: string;
  paymentTerms?: string;
  paymentMethod?: string;
  notes?: string;
}

const ORDER_STATUSES: { value: OrderStatus; label: string; color: string }[] = [
  { value: 'draft', label: 'Tervezet', color: 'bg-gray-100 text-gray-800' },
  { value: 'pending', label: 'Függőben', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'confirmed', label: 'Megerősítve', color: 'bg-blue-100 text-blue-800' },
  { value: 'production', label: 'Gyártás alatt', color: 'bg-purple-100 text-purple-800' },
  { value: 'ready', label: 'Kész', color: 'bg-green-100 text-green-800' },
  { value: 'shipped', label: 'Szállítva', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'delivered', label: 'Kiszállítva', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'cancelled', label: 'Törölve', color: 'bg-red-100 text-red-800' }
];

const OrderForm: React.FC<OrderFormProps> = ({ order, isOpen, onClose, onSave }) => {
  const { t } = useLanguage();
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<OrderFormData>();

  useEffect(() => {
    if (order) {
      reset({
        status: order.status,
        requestedDeliveryDate: order.requestedDeliveryDate,
        confirmedDeliveryDate: order.confirmedDeliveryDate,
        deliveryAddress: order.deliveryAddress,
        deliveryInstructions: order.deliveryInstructions,
        paymentTerms: order.paymentTerms,
        paymentMethod: order.paymentMethod,
        notes: order.notes
      });
    }
  }, [order, reset]);

  const onSubmit = (data: OrderFormData) => {
    if (!order) return;
    onSave(order.id, data);
    onClose();
  };

  if (!order) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Megrendelés szerkesztése - ${order.number}`}
      size="6xl"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
        {/* Alapinformációk */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <ShoppingCartIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Megrendelés információk</h3>
          </div>
          <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Megrendelés száma:</span>
                  <span>{order.number}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Ügyfél:</span>
                  <span>{order.customerName}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Megrendelés dátuma:</span>
                  <span>{new Date(order.orderDate).toLocaleDateString('hu-HU')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Összeg:</span>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {formatCurrency(order.totalAmount, order.currency)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Státusz */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <DocumentTextIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Státusz kezelése</h3>
          </div>
          <div className="p-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Státusz *
                </label>
                <select
                  {...register('status', { required: 'A státusz megadása kötelező' })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  {ORDER_STATUSES.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dátumok */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <CalendarIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Szállítási dátumok</h3>
          </div>
          <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kért szállítási dátum
                  </label>
                  <input
                    type="date"
                    {...register('requestedDeliveryDate')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Megerősített szállítási dátum
                  </label>
                  <input
                    type="date"
                    {...register('confirmedDeliveryDate')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Szállítási adatok */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <TruckIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Szállítási információk</h3>
          </div>
          <div className="p-4">
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Szállítási cím
                </label>
                <textarea
                  {...register('deliveryAddress')}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Szállítási cím..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Szállítási utasítások
                </label>
                <textarea
                  {...register('deliveryInstructions')}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Szállítási utasítások..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Fizetési adatok */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <CurrencyDollarIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Fizetési feltételek</h3>
          </div>
          <div className="p-4">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fizetési feltételek
                  </label>
                  <input
                    type="text"
                    {...register('paymentTerms')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="pl. 30 nap"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fizetési mód
                  </label>
                  <select
                    {...register('paymentMethod')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">Válasszon...</option>
                    <option value="bank_transfer">Banki átutalás</option>
                    <option value="cash">Készpénz</option>
                    <option value="card">Bankkártya</option>
                    <option value="check">Csekk</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Megjegyzések */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <DocumentTextIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Megjegyzések</h3>
          </div>
          <div className="p-4">
              <div>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Megjegyzések..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form gombok */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Mégse
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Mentés
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default OrderForm; 